import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import {
  brokerFor,
  brokeredOrigin,
  type IntegrationCapability,
  type IntegrationSystemCode,
} from '@/types/platform';
import styles from './platform-ui.module.css';

/**
 * What a connection actually carries, and where each thing really comes from.
 *
 * CAPABILITIES ARE THE POINT, NOT DECORATION
 * A list of connected systems answers "what have we plugged in". The question an
 * operator has is "can we send this parent a WhatsApp message", and those are not
 * the same question, because one connection frequently answers several of them.
 * SMS, WhatsApp, email, payments and the CrediSure synchronisation all arrive
 * through the CSKY Digital Switch: one host, one API key, one HMAC secret, one
 * tenant header. There is one DIGITAL_SWITCH row and it carries five capabilities.
 *
 * WHY THE BROKER IS NAMED ON THE CHIP
 * Because otherwise an operator looking for "the WhatsApp connection" finds
 * nothing, concludes WhatsApp is not configured, and registers a second row
 * describing the same wire. Two rows for one path disagree the first time the
 * Switch's address changes, and the disagreement surfaces as messages that
 * silently stop arriving. Saying "WhatsApp — brokered through the Digital Switch"
 * on the chip is what stops the search before it starts.
 */

interface CapabilityChipsProps {
  /** The connection carrying these capabilities. Decides what counts as brokered. */
  systemCode: IntegrationSystemCode;
  capabilities: readonly IntegrationCapability[];
  /** Shown when the list is empty, instead of nothing at all. */
  emptyLabel?: string;
}

export function CapabilityChips({ systemCode, capabilities, emptyLabel }: CapabilityChipsProps) {
  const { t } = useTranslation();

  if (capabilities.length === 0) {
    return (
      <p className={styles.capabilityEmpty}>
        {emptyLabel ?? t('platform.connections.capabilitiesNone')}
      </p>
    );
  }

  return (
    <ul className={styles.capabilityList}>
      {capabilities.map((capability) => {
        const origin = brokeredOrigin(systemCode, capability);
        const label = t(`platform.capability.${capability}`, { defaultValue: capability });

        if (!origin) {
          return (
            <li key={capability} className={styles.capabilityChip}>
              {label}
            </li>
          );
        }

        const originLabel = t(`platform.system.${origin}`, { defaultValue: origin });
        const carrierLabel = t(`platform.system.${systemCode}`, { defaultValue: systemCode });

        return (
          <li key={capability} className={`${styles.capabilityChip} ${styles.capabilityBrokered}`}>
            <Icon name="switch" size={12} aria-hidden="true" />
            {label}
            {/* Spelled out rather than left to an icon: the whole failure this
                prevents is somebody not realising the icon meant anything. */}
            <span className={styles.capabilityVia}>
              {t('platform.connections.capabilityVia', { system: originLabel, broker: carrierLabel })}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Shown when somebody is about to register a system that is normally brokered.
 *
 * Not a blocker. An un-brokered WhatsApp connection is a legitimate, rare thing
 * and the console must be able to describe it. But it is far more often a second
 * description of a wire that already exists, so it is stated before the row is
 * created rather than discovered during the incident where the two disagree.
 */
export function BrokeredSystemNotice({ systemCode }: { systemCode: IntegrationSystemCode }) {
  const { t } = useTranslation();
  const broker = brokerFor(systemCode);
  if (!broker) return null;

  return (
    <div className={styles.brokerNotice} role="note">
      <Icon name="warning" size={16} aria-hidden="true" />
      <div>
        <p className={styles.brokerNoticeTitle}>
          {t('platform.connections.brokerTitle', {
            system: t(`platform.system.${systemCode}`, { defaultValue: systemCode }),
            broker: t(`platform.system.${broker}`, { defaultValue: broker }),
          })}
        </p>
        <p className={styles.brokerNoticeBody}>{t('platform.connections.brokerBody')}</p>
      </div>
    </div>
  );
}

/**
 * A credential reference, rendered as the pointer it is.
 *
 * Shown in full and never masked. Masking a `vault://` path would imply it is
 * sensitive, which teaches exactly the wrong lesson — the whole reason a pointer
 * is stored instead of a key is that the pointer is safe to copy into a ticket,
 * and being able to read it is how a rotation is confirmed without anybody
 * fetching the value.
 */
export function CredentialReference({ reference }: { reference?: string | null }) {
  const { t } = useTranslation();

  if (!reference) {
    return <span className={styles.credentialAbsent}>{t('platform.connections.credentialNone')}</span>;
  }

  return (
    <code className={styles.credentialReference} title={t('platform.connections.credentialPointerHint')}>
      {reference}
    </code>
  );
}
